import { DEFAULT_VALUES, MAIL_REGEX } from 'src/app/shared/common/constants';
import { stringToRutFormatted } from '../../core/utils/formatters';
import { toTitleCaseOrDefault } from '../../core/utils/functions';
import { EMPLOYER_SUMMARY_LABELS } from '../constants/confirm.constant';
import { EMPLOYER_LABELS } from '../constants/employer.constant';
import {
  IEmployerLookupResult,
  IEmployerRawFromService,
} from '../models/subscription-api.model';
import {
  IAddEmployerData,
  IDetailRow,
  IEmployerOption,
  IEmployerSelection,
} from '../models/subscription.model';

export const isValidMail = (mail: string): boolean =>
  new RegExp(MAIL_REGEX).test(mail ?? '');

const formatEmployerRut = (employer: IEmployerRawFromService): string => {
  if (!employer.employerRut) return DEFAULT_VALUES.NO_DATA_FROM_SERVICE;

  const rutWithoutLeadingZeros = employer.employerRut.replace(/^0+/, '');

  return stringToRutFormatted(
    rutWithoutLeadingZeros + employer.employerVerificationDigit
  );
};

export const toEmployerOption = (
  employer: IEmployerRawFromService
): IEmployerOption => {
  const rut = formatEmployerRut(employer);
  const name = toTitleCaseOrDefault(employer.employerName);
  const email = isValidMail(employer.employerEmail) ? employer.employerEmail : '';

  return {
    id: `${employer.employerRut}${employer.employerVerificationDigit}`,
    name,
    rut,
    details: [
      { label: EMPLOYER_LABELS.businessName, value: name },
      {
        label: EMPLOYER_LABELS.activity,
        value: toTitleCaseOrDefault(employer.employerActivity),
      },
      {
        label: EMPLOYER_LABELS.address,
        value: toTitleCaseOrDefault(employer.employerAddress),
      },
      {
        label: EMPLOYER_LABELS.region,
        value: toTitleCaseOrDefault(employer.employerRegion),
      },
      {
        label: EMPLOYER_LABELS.commune,
        value: toTitleCaseOrDefault(employer.employerCommune),
      },
      {
        label: EMPLOYER_LABELS.phone,
        value: employer.employerPhone || DEFAULT_VALUES.NO_DATA_FROM_SERVICE,
      },
      { label: EMPLOYER_LABELS.rut, value: rut },
    ],
    email,
    emailSaved: !!email,
    emailTouched: false,
  };
};

export const toAddEmployerData = (
  result: IEmployerLookupResult
): IAddEmployerData => ({
  employerFullRut: stringToRutFormatted(
    `${result.employerRut}${result.employerVerificationDigit}`
  ),
  employerName: toTitleCaseOrDefault(result.employerName),
  employerActivity: toTitleCaseOrDefault(result.employerActivity),
  employerRegion: toTitleCaseOrDefault(result.employerRegion),
  employerCommune: toTitleCaseOrDefault(result.employerCommune),
  employerCity: toTitleCaseOrDefault(result.employerCity),
  employerAddress: toTitleCaseOrDefault(result.employerAddress),
  employerPhone: result.employerPhone || DEFAULT_VALUES.NO_DATA_FROM_SERVICE,
  employerEmail: result.employerEmail ?? '',
});

export const toEmployerSelectionFromOption = (
  option: IEmployerOption
): IEmployerSelection => ({
  name: option.name,
  fullRut: option.rut,
  email: option.email,
  address: '',
  details: option.details,
});

const summaryRows = (data: IAddEmployerData): IDetailRow[] =>
  [
    { label: EMPLOYER_SUMMARY_LABELS.businessName, value: data.employerName },
    { label: EMPLOYER_SUMMARY_LABELS.activity, value: data.employerActivity },
    { label: EMPLOYER_SUMMARY_LABELS.address, value: data.employerAddress },
    { label: EMPLOYER_SUMMARY_LABELS.region, value: data.employerRegion },
    { label: EMPLOYER_SUMMARY_LABELS.commune, value: data.employerCommune },
    { label: EMPLOYER_SUMMARY_LABELS.city, value: data.employerCity },
    { label: EMPLOYER_SUMMARY_LABELS.phone, value: data.employerPhone },
    { label: EMPLOYER_SUMMARY_LABELS.rut, value: data.employerFullRut },
  ].filter((row) => !!row.value);

export const toEmployerSelectionFromLookup = (
  data: IAddEmployerData
): IEmployerSelection => ({
  name: data.employerName,
  fullRut: data.employerFullRut,
  email: data.employerEmail,
  address: data.employerAddress,
  details: summaryRows(data),
  lookup: data,
});
